import { useState } from "react";
import { View } from "react-native";
import { SvgXml } from "react-native-svg";
import { Button } from "~/components/ui/Button";
import { BodyText, CaptionText, Heading3 } from "~/components/ui/Text";
import { TextField } from "~/components/ui/TextField";
import {
  disableTotp,
  startTotpSetup,
  useTotpStatus,
  verifyTotpCode,
  type TotpSetup,
} from "~/lib/totp";
import { spacing, useTheme } from "~/styles/theme";

/** Account settings section: turn optional two-factor authentication on or off. */
export function TwoFactorCard() {
  const { colors } = useTheme();
  const status = useTotpStatus();
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  };

  const begin = () =>
    run(async () => setSetup(await startTotpSetup("Durance authenticator")));
  const confirm = () =>
    run(async () => {
      await verifyTotpCode(code);
      setSetup(null);
      setCode("");
      await status.refresh();
    });
  const turnOff = () =>
    run(async () => {
      await disableTotp();
      await status.refresh();
    });
  const cancel = () => {
    setSetup(null);
    setCode("");
    setError("");
  };

  return (
    <View style={{ gap: spacing.sm }}>
      <Heading3>Two-factor authentication</Heading3>
      {status.loading ? (
        <CaptionText color={colors.muted}>Checking…</CaptionText>
      ) : setup ? (
        <>
          <CaptionText color={colors.muted}>
            Scan this code with an authenticator app, or type the key in by
            hand. Then enter the 6-digit code it shows.
          </CaptionText>
          <View style={{ alignSelf: "center", width: 200, height: 200 }}>
            <SvgXml xml={setup.qrSvg} width="100%" height="100%" />
          </View>
          <TextField
            label="Setup key"
            value={setup.secret}
            editable={false}
            selectTextOnFocus
          />
          <TextField
            label="6-digit code"
            value={code}
            onChangeText={setCode}
            keyboardType="number-pad"
            autoComplete="one-time-code"
            maxLength={6}
          />
          {error ? <BodyText color={colors.danger}>{error}</BodyText> : null}
          <Button
            label={busy ? "Checking…" : "Turn on two-factor"}
            disabled={busy || code.trim().length !== 6}
            fullWidth
            onPress={() => void confirm()}
          />
          <Button label="Cancel" variant="ghost" fullWidth onPress={cancel} />
        </>
      ) : status.enabled ? (
        <>
          <CaptionText color={colors.muted}>
            On. Signing in asks for a code from your authenticator app.
          </CaptionText>
          {error ? <BodyText color={colors.danger}>{error}</BodyText> : null}
          <Button
            label={busy ? "Working…" : "Turn off two-factor"}
            variant="danger"
            disabled={busy}
            fullWidth
            onPress={() => void turnOff()}
          />
        </>
      ) : (
        <>
          <CaptionText color={colors.muted}>
            Optional. Add a code from an authenticator app to sign-in for extra
            protection.
          </CaptionText>
          {error ? <BodyText color={colors.danger}>{error}</BodyText> : null}
          <Button
            label={busy ? "Working…" : "Set up two-factor"}
            disabled={busy}
            fullWidth
            onPress={() => void begin()}
          />
        </>
      )}
    </View>
  );
}
